/**
 * LVGL-conformant box-shadow geometry and rasterization for the shared
 * main-object rect.
 *
 * Like borderGeometry.ts this is a pure module — no Konva, no canvas, no widget
 * knowledge — so every part of it is unit testable in the plain `node` test
 * environment. LvglShadowShape is its only renderer, and every widget reaches
 * that shape through the single `this.shadow` created in ObjectGroup, so there
 * is no per-widget shadow code.
 *
 * The model is taken from LVGL 7.11's lv_draw_rect.c `draw_shadow()` (the
 * version openHASP vendors). Canvas/Konva `shadowBlur` is deliberately NOT used:
 * it is a Gaussian blur whose falloff and extent do not match LVGL at all.
 *
 * LVGL builds the shadow in three steps:
 *
 *   1. A "core" rectangle — the object's box expanded by `shadow_spread` on
 *      every side and translated by (`shadow_ofs_x`, `shadow_ofs_y`):
 *
 *          core.x1 = coords.x1 + ofs_x - spread
 *          core.x2 = coords.x2 + ofs_x + spread     (and likewise for y)
 *
 *      It is masked with lv_draw_mask_radius using the object's own `radius`,
 *      clamped to half the CORE's short side — spread does not increase the
 *      radius, so a fully-rounded object (radius 999) stays a circle/stadium at
 *      any spread.
 *
 *   2. That mask is box-blurred, separably: one horizontal pass and one
 *      vertical pass, each with a window of exactly `shadow_width` samples
 *      (shadow_blur_corner()). A single box pass over a step edge produces a
 *      LINEAR ramp of width `shadow_width` straddling the core edge — which is
 *      why tuning Konva's Gaussian `shadowBlur` can never match it. LVGL's
 *      window is asymmetric for even widths (s_left = sw/2 - 1, s_right = sw/2)
 *      and symmetric for odd ones; reproduced verbatim below.
 *
 *   3. The blurred mask is drawn in `shadow_color` scaled by `shadow_opa`.
 *
 *   The bounding box is core ± (shadow_width / 2 + 1), matching LVGL's
 *   `shadow_area`, so the fade region lives half outside and half inside the
 *   core edge — NOT entirely outside it.
 *
 * Visibility follows LVGL's early-outs verbatim (see resolveShadowGeometry).
 * Notably `shadow_width == 0` means no shadow at all, even with a non-zero
 * spread or offset — spread alone never draws.
 */

/** LVGL's LV_OPA_COVER — the value an unset shadow_opa resolves to. */
export const SHADOW_OPA_COVER = 255;

/** LVGL's LV_OPA_MIN — `shadow_opa <= LV_OPA_MIN` draws nothing. */
export const SHADOW_OPA_MIN = 2;

export interface ShadowRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ShadowInput {
    /** The object's box, always local (0,0)-anchored. */
    width: number;
    height: number;
    /** The object's effective radius — the same value the background/border use. */
    radius?: number | null;
    shadowWidth?: number | null;
    shadowSpread?: number | null;
    ofsX?: number | null;
    ofsY?: number | null;
    /** 0-255; unset resolves to LV_OPA_COVER, matching lv_draw_rect_dsc_init(). */
    opa?: number | null;
}

export interface ShadowGeometry {
    /** False when LVGL would draw nothing at all. */
    visible: boolean;
    /** Spread-expanded, offset rectangle, in object-local coordinates. */
    core: ShadowRect;
    /** Radius actually applied to `core` (object radius clamped to the core). */
    radius: number;
    /** Bitmap extent: core padded by blur/2 + 1 on every side. May be negative. */
    bounds: ShadowRect;
    /** LVGL's `shadow_width` — the box-blur window, in object units. */
    blur: number;
    /** 0-255. */
    opa: number;
}

export interface ShadowAlphaMap {
    alpha: Float32Array;
    width: number;
    height: number;
    /** Device pixels per object unit this map was rasterized at. */
    scale: number;
}

export interface ShadowRGB {
    r: number;
    g: number;
    b: number;
    /** 0-1, from a #rrggbbaa / rgba() colour; multiplied into shadow_opa. */
    a: number;
}

/**
 * Coerce a possibly-missing / string / NaN value to a finite number. `null` and
 * `''` count as missing, not as 0 — an unset property must fall back to LVGL's
 * default (which for shadow_opa is LV_OPA_COVER, not transparent).
 */
function num(value: unknown, fallback: number): number {
    if (value === null || value === undefined || value === '') return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Resolve the shadow geometry for one main object. Pure: same inputs always
 * produce the same output, and no rounding to device pixels happens here (the
 * stage transform may place the shape on fractional coordinates at odd zoom).
 */
export function resolveShadowGeometry(input: ShadowInput): ShadowGeometry {
    const width = Math.max(0, num(input.width, 0));
    const height = Math.max(0, num(input.height, 0));
    // shadow_width/shadow_spread are lv_coord_t in firmware; width is clamped
    // non-negative by the schema, spread is signed and may shrink the core.
    const blur = Math.max(0, Math.round(num(input.shadowWidth, 0)));
    const spread = Math.round(num(input.shadowSpread, 0));
    const ofsX = Math.round(num(input.ofsX, 0));
    const ofsY = Math.round(num(input.ofsY, 0));
    const opa = clamp(Math.round(num(input.opa, SHADOW_OPA_COVER)), 0, 255);

    const core: ShadowRect = {
        x: ofsX - spread,
        y: ofsY - spread,
        width: width + 2 * spread,
        height: height + 2 * spread,
    };

    // Radius is the OBJECT's radius clamped to the core — spread never inflates
    // it. This is what keeps radius 999 a true circle/stadium at any spread,
    // and what keeps radius >= half the box correct.
    const radius = clamp(num(input.radius, 0), 0, Math.min(core.width, core.height) / 2);

    const ext = Math.floor(blur / 2) + 1;
    const bounds: ShadowRect = {
        x: core.x - ext,
        y: core.y - ext,
        width: core.width + 2 * ext,
        height: core.height + 2 * ext,
    };

    // LVGL's early-outs, verbatim (lv_draw_rect.c draw_shadow):
    //   shadow_width == 0                                   -> nothing
    //   shadow_opa <= LV_OPA_MIN                             -> nothing
    //   shadow_width == 1 && spread <= 0 && no offset        -> nothing
    // The third is LVGL's "a 1px blur exactly on the object edge is invisible
    // behind the object anyway" shortcut, not an approximation.
    const degenerate =
        blur === 0 ||
        opa <= SHADOW_OPA_MIN ||
        (blur === 1 && spread <= 0 && ofsX === 0 && ofsY === 0) ||
        core.width <= 0 ||
        core.height <= 0 ||
        width <= 0 ||
        height <= 0;

    return { visible: !degenerate, core, radius, bounds, blur, opa };
}

/**
 * Signed distance from a point to a rounded rectangle — negative inside.
 * The standard rounded-box SDF; used to get a 1-pixel anti-aliased coverage
 * ramp equivalent to lv_draw_mask_radius's own edge anti-aliasing.
 */
function roundedRectSDF(
    px: number,
    py: number,
    rect: ShadowRect,
    radius: number,
): number {
    const hw = rect.width / 2;
    const hh = rect.height / 2;
    const r = clamp(radius, 0, Math.min(hw, hh));
    const qx = Math.abs(px - (rect.x + hw)) - hw + r;
    const qy = Math.abs(py - (rect.y + hh)) - hh + r;
    return Math.min(Math.max(qx, qy), 0) + Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) - r;
}

/**
 * Rasterize the un-blurred core mask into a coverage buffer (0..1 per pixel),
 * in the geometry's `bounds` frame at `scale` device pixels per object unit.
 * Exported so the mask can be asserted independently of the blur.
 */
export function rasterizeCoreMask(geom: ShadowGeometry, scale = 1): ShadowAlphaMap {
    const width = Math.max(1, Math.round(geom.bounds.width * scale));
    const height = Math.max(1, Math.round(geom.bounds.height * scale));
    const alpha = new Float32Array(width * height);

    // Everything in device pixels relative to bounds' top-left.
    const rect: ShadowRect = {
        x: (geom.core.x - geom.bounds.x) * scale,
        y: (geom.core.y - geom.bounds.y) * scale,
        width: geom.core.width * scale,
        height: geom.core.height * scale,
    };
    const radius = geom.radius * scale;

    for (let y = 0; y < height; y++) {
        const row = y * width;
        const py = y + 0.5;
        for (let x = 0; x < width; x++) {
            // coverage = 1 well inside, 0 well outside, linear across one pixel
            alpha[row + x] = clamp(0.5 - roundedRectSDF(x + 0.5, py, rect, radius), 0, 1);
        }
    }
    return { alpha, width, height, scale };
}

/**
 * One separable box-blur pass pair, exactly as LVGL 7.11's
 * shadow_blur_corner(): window of `window` samples, asymmetric to the left for
 * even widths, edge-clamped outside the buffer (the buffer is padded so the
 * clamped values are the true 0 / 1 continuation of the mask).
 *
 * A single pass per axis is the point — it yields LVGL's LINEAR falloff. Two or
 * more passes would approach a Gaussian, which is exactly what this module
 * exists to avoid.
 */
export function boxBlurAlpha(
    src: Float32Array,
    width: number,
    height: number,
    window: number,
): Float32Array {
    const w = Math.max(1, Math.round(window));
    if (w <= 1) return Float32Array.from(src);

    const sRight = w >> 1;
    const sLeft = (w & 1) === 0 ? (w >> 1) - 1 : w >> 1;
    const inv = 1 / w;

    const horizontal = new Float32Array(src.length);
    for (let y = 0; y < height; y++) {
        const row = y * width;
        let sum = 0;
        for (let i = -sLeft; i <= sRight; i++) sum += src[row + clamp(i, 0, width - 1)];
        horizontal[row] = sum * inv;
        for (let x = 1; x < width; x++) {
            sum += src[row + clamp(x + sRight, 0, width - 1)];
            sum -= src[row + clamp(x - sLeft - 1, 0, width - 1)];
            horizontal[row + x] = sum * inv;
        }
    }

    const out = new Float32Array(src.length);
    for (let x = 0; x < width; x++) {
        let sum = 0;
        for (let i = -sLeft; i <= sRight; i++) sum += horizontal[clamp(i, 0, height - 1) * width + x];
        out[x] = sum * inv;
        for (let y = 1; y < height; y++) {
            sum += horizontal[clamp(y + sRight, 0, height - 1) * width + x];
            sum -= horizontal[clamp(y - sLeft - 1, 0, height - 1) * width + x];
            out[y * width + x] = sum * inv;
        }
    }
    return out;
}

/** The complete LVGL shadow mask: core coverage, box-blurred by shadow_width. */
export function rasterizeShadowAlpha(geom: ShadowGeometry, scale = 1): ShadowAlphaMap {
    const map = rasterizeCoreMask(geom, scale);
    return {
        ...map,
        alpha: boxBlurAlpha(map.alpha, map.width, map.height, geom.blur * scale),
    };
}

/**
 * Parse a designer colour into components. Accepts #rgb, #rrggbb, #rrggbbaa and
 * rgb()/rgba(); anything else falls back to opaque black, matching LVGL's
 * default shadow_color. Kept here (not in the Konva shape) so it is testable.
 */
export function parseShadowColor(color: unknown): ShadowRGB {
    const fallback: ShadowRGB = { r: 0, g: 0, b: 0, a: 1 };
    if (typeof color !== 'string') return fallback;
    const value = color.trim();

    const hex = /^#([0-9a-f]{3,8})$/i.exec(value);
    if (hex) {
        let h = hex[1];
        if (h.length === 3) h = h.split('').map((c) => c + c).join('');
        if (h.length !== 6 && h.length !== 8) return fallback;
        return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
            a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1,
        };
    }

    const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(value);
    if (rgb) {
        return {
            r: clamp(Math.round(Number(rgb[1])), 0, 255),
            g: clamp(Math.round(Number(rgb[2])), 0, 255),
            b: clamp(Math.round(Number(rgb[3])), 0, 255),
            a: rgb[4] === undefined ? 1 : clamp(Number(rgb[4]), 0, 1),
        };
    }
    return fallback;
}

/**
 * Flatten a mask into non-premultiplied RGBA bytes, ready for putImageData.
 * Opacity is applied once, here — the mask itself stays a pure coverage map.
 */
export function composeShadowRGBA(map: ShadowAlphaMap, color: ShadowRGB, opa: number): Uint8ClampedArray {
    const out = new Uint8ClampedArray(map.width * map.height * 4);
    const scale = (clamp(opa, 0, 255) / 255) * clamp(color.a, 0, 1) * 255;
    for (let i = 0, p = 0; i < map.alpha.length; i++, p += 4) {
        out[p] = color.r;
        out[p + 1] = color.g;
        out[p + 2] = color.b;
        out[p + 3] = map.alpha[i] * scale;
    }
    return out;
}
